import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Twitter, ArrowUpRight, Shield } from 'lucide-react';
import { COMPANY } from '../mock';

const Footer = () => {
  return (
    <footer className="bg-[#0A1628] text-white relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#D4A24C]/5 blur-3xl" />

      {/* CTA */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
          <div>
            <h3 className="text-3xl md:text-4xl font-extrabold leading-tight">Ready to ship your vehicle?</h3>
            <p className="text-white/70 mt-2">Get a transparent quote in 10 seconds — no obligation, no upfront payment.</p>
          </div>
          <div className="flex gap-3">
            <a href="/#quote" className="inline-flex items-center gap-1 btn-accent px-6 py-3.5 rounded-lg font-bold">
              Get a Free Quote <ArrowUpRight className="w-4 h-4" />
            </a>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 border border-white/20 hover:border-[#D4A24C] px-6 py-3.5 rounded-lg font-semibold transition-colors">
              <Phone className="w-4 h-4" /> Call us
            </a>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
              <img src={COMPANY.logo} alt="Excelence Inc" className="w-full h-full object-cover scale-150" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-extrabold text-lg tracking-tight">
                <span className="text-[#2AA8E8]">EXCELENCE</span>{' '}
                <span className="gold-text">INC.</span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-semibold">{COMPANY.mc} · {COMPANY.dot}</span>
            </div>
          </Link>
          <p className="mt-5 text-sm text-white/70 leading-relaxed">
            Professional transportation & logistics, founded on more than a decade of on-the-road, dispatch and brokerage experience.
          </p>
          <div className="flex gap-2 mt-6">
            {[Facebook, Instagram, Linkedin, Twitter].map((Icon, i) => (
              <a key={i} href="#" className="w-9 h-9 rounded-lg border border-white/15 flex items-center justify-center hover:bg-[#2AA8E8] hover:text-white hover:border-[#2AA8E8] transition-colors">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 w-fit">
            <Shield className="w-4 h-4 text-[#2AA8E8]" />
            <span className="text-xs font-bold text-white">{COMPANY.mc}</span>
            <span className="text-white/30">·</span>
            <span className="text-xs font-bold text-white">{COMPANY.dot}</span>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4A24C] mb-4">Services</h4>
          <ul className="space-y-3 text-sm text-white/80">
            <li><a href="/services" className="hover:text-[#D4A24C] transition-colors">Open Auto Transport</a></li>
            <li><a href="/services" className="hover:text-[#D4A24C] transition-colors">Enclosed Auto Transport</a></li>
            <li><a href="/services" className="hover:text-[#D4A24C] transition-colors">Expedited Shipping</a></li>
            <li><a href="/services" className="hover:text-[#D4A24C] transition-colors">Motorcycle Shipping</a></li>
            <li><a href="/services" className="hover:text-[#D4A24C] transition-colors">Classic & Luxury Cars</a></li>
          </ul>
        </div>

        <div>
          <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4A24C] mb-4">Quick Links</h4>
          <ul className="space-y-3 text-sm text-white/80">
            <li><a href="/#quote" className="hover:text-[#D4A24C] transition-colors">Get Quote</a></li>
            <li><Link to="/services" className="hover:text-[#D4A24C] transition-colors">Services</Link></li>
            <li><Link to="/partners" className="hover:text-[#D4A24C] transition-colors">Partners & Network</Link></li>
            <li><Link to="/drivers" className="hover:text-[#D4A24C] transition-colors">Driver Waitlist</Link></li>
            <li><Link to="/careers" className="hover:text-[#D4A24C] transition-colors">Careers</Link></li>
            <li><Link to="/contact" className="hover:text-[#D4A24C] transition-colors">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4A24C] mb-4">Contact</h4>
          <ul className="space-y-4 text-sm text-white/80">
            <li className="flex gap-3">
              <Phone className="w-4 h-4 mt-0.5 text-[#D4A24C]" />
              <a href={COMPANY.phoneHref} className="hover:text-[#D4A24C] transition-colors">{COMPANY.phone}</a>
            </li>
            <li className="flex gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-[#D4A24C]" />
              <a href={COMPANY.emailHref} className="hover:text-[#D4A24C] transition-colors break-all">{COMPANY.email}</a>
            </li>
            <li className="flex gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[#D4A24C]" />
              <span>{COMPANY.location}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4A24C] mb-3">Service Areas · Nationwide Car Shipping</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/70">
            <Link to="/car-shipping-texas" className="hover:text-[#D4A24C]">Car Shipping Texas</Link>
            <Link to="/car-shipping-florida" className="hover:text-[#D4A24C]">Car Shipping Florida</Link>
            <Link to="/car-shipping-california" className="hover:text-[#D4A24C]">Car Shipping California</Link>
            <Link to="/car-shipping-new-york" className="hover:text-[#D4A24C]">Car Shipping New York</Link>
            <Link to="/car-shipping-georgia" className="hover:text-[#D4A24C]">Car Shipping Georgia</Link>
            <Link to="/car-shipping-arizona" className="hover:text-[#D4A24C]">Car Shipping Arizona</Link>
            <Link to="/car-shipping-illinois" className="hover:text-[#D4A24C]">Car Shipping Illinois</Link>
            <Link to="/car-shipping-nevada" className="hover:text-[#D4A24C]">Car Shipping Nevada</Link>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col md:flex-row gap-3 md:justify-between text-xs text-white/60">
          <div>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved. · {COMPANY.mc} · {COMPANY.dot}</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-[#D4A24C] transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-[#D4A24C] transition-colors">Terms of Use</a>
            <a href="#" className="hover:text-[#D4A24C] transition-colors">SMS Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
